import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AppShell, { SectionCard, StatCard } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  MapPin, Buildings, Plus, ArrowClockwise, Warning, Path, ShieldCheck,
  ArrowRight, Trash, PencilSimple, Camera, Clock, Gear,
} from "@phosphor-icons/react";
import { isoDateIST } from "../lib/utils";
import { DateInput } from "../components/ui/date-input";

// ---- Leaflet defaults ----
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const dotIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="width:18px;height:18px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
  iconSize: [18, 18], iconAnchor: [9, 9],
});
const partyIcon = L.divIcon({
  className: "",
  html: `<div style="width:24px;height:24px;background:#6366f1;border:3px solid #fff;border-radius:6px;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-size:14px;font-weight:bold">P</div>`,
  iconSize: [24, 24], iconAnchor: [12, 12],
});

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
const fmtKm = (n) => n == null ? "—" : `${Number(n).toFixed(2)} km`;

const PARTY_TYPES = [
  { value: "client",          label: "Client" },
  { value: "site",            label: "Site / Project" },
  { value: "warehouse",       label: "Warehouse" },
  { value: "vendor",          label: "Vendor" },
  { value: "internal_office", label: "Internal office" },
];

// ============================================================================
// Module gate helper — returns a friendly "module off" panel
// ============================================================================
function ModuleOffPanel({ title, message }) {
  return (
    <AppShell title={title}>
      <SectionCard testid="section-field-off">
        <div className="text-center py-16 px-6 max-w-2xl mx-auto">
          <MapPin size={48} className="mx-auto text-zinc-300 mb-3" />
          <div className="font-display text-2xl font-bold mb-2">Field Force Geo-Tracking is off</div>
          <p className="text-sm text-zinc-600 mb-6">
            {message || "Enable this module to auto-detect client visits, run a privacy-first live map, and audit field-employee productivity. ₹40/employee/month retail, DPDP-compliant."}
          </p>
          <Button data-testid="field-go-modules" onClick={() => window.location.href = "/app/billing"}>
            Enable in Billing & Modules →
          </Button>
        </div>
      </SectionCard>
    </AppShell>
  );
}

function useGate(loadFn) {
  const [state, setState] = useState({ loading: true, off: false, error: null });
  const reload = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await loadFn();
      setState({ loading: false, off: false, data });
    } catch (e) {
      if (e?.response?.status === 402) setState({ loading: false, off: true, data: null });
      else setState({ loading: false, off: false, error: e, data: null });
    }
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line
  return { ...state, reload, setState };
}

// ============================================================================
// 1. PARTIES PAGE — /app/field/parties
// ============================================================================
export function FieldParties() {
  const { user } = useAuth();
  const isHR = ["super_admin", "company_admin", "country_head", "region_head", "branch_manager"].includes(user?.role);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleOff, setModuleOff] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [editing, setEditing] = useState(null); // null | object (open dialog with this row, or {} for new)

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/field/parties", { params: { status: statusFilter } });
      setParties(data || []);
      setModuleOff(false);
    } catch (e) {
      if (e?.response?.status === 402) setModuleOff(true);
      else toast.error("Failed to load parties");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q.trim()) return parties;
    const t = q.toLowerCase();
    return parties.filter(p =>
      (p.name || "").toLowerCase().includes(t) ||
      (p.code || "").toLowerCase().includes(t) ||
      (p.city || "").toLowerCase().includes(t)
    );
  }, [parties, q]);

  if (moduleOff) return <ModuleOffPanel title="Field — Parties" />;

  return (
    <AppShell title="Field — Party Master">
      <div className="space-y-4">
        <SectionCard
          title="Customer / site directory"
          subtitle="Geo-fenced locations your field team visits. Visits are auto-detected when an employee enters the geofence."
          action={
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                data-testid="party-search"
                placeholder="Search by name, code, city…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full sm:w-64"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="party-status-filter" className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              {isHR && (
                <Button data-testid="party-add-btn" onClick={() => setEditing({})}>
                  <Plus size={16} /> New party
                </Button>
              )}
            </div>
          }
          testid="section-parties-list"
        >
          {loading ? (
            <div className="text-sm text-zinc-500 py-6">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-zinc-500 py-12 text-center">
              <Buildings size={32} className="mx-auto text-zinc-300 mb-2" />
              {parties.length === 0
                ? "No parties yet. Add your first client or site."
                : "No matches for your search."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b">
                <tr>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-left py-2">Location</th>
                  <th className="text-left py-2">Geofence</th>
                  <th className="text-left py-2">Status</th>
                  {isHR && <th className="text-right py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b last:border-b-0" data-testid={`party-row-${p.id}`}>
                    <td className="py-2">
                      <div className="font-medium">{p.name}</div>
                      {p.code && <div className="text-xs text-zinc-500 font-mono">{p.code}</div>}
                    </td>
                    <td className="py-2 capitalize">{(p.type || "client").replace("_", " ")}</td>
                    <td className="py-2 text-xs text-zinc-600">
                      {p.city ? `${p.city}${p.state ? ", " + p.state : ""}` : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="py-2 text-xs text-zinc-600">
                      {p.geo ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} /> {p.geo.lat?.toFixed(4)}, {p.geo.lng?.toFixed(4)} · {p.geo.radius_m || 100} m
                        </span>
                      ) : <span className="text-amber-600">No geo</span>}
                    </td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                        {p.status}
                      </span>
                    </td>
                    {isHR && (
                      <td className="py-2 text-right">
                        <Button data-testid={`party-edit-${p.id}`} variant="ghost" size="sm" onClick={() => setEditing(p)}>
                          <PencilSimple size={14} />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {editing != null && (
        <PartyDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </AppShell>
  );
}

// ---- Map click picker for the party dialog ----
function ClickPicker({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function PartyDialog({ row, onClose, onSaved }) {
  const isNew = !row?.id;
  const [name, setName] = useState(row?.name || "");
  const [code, setCode] = useState(row?.code || "");
  const [type, setType] = useState(row?.type || "client");
  const [address, setAddress] = useState(row?.address || "");
  const [city, setCity] = useState(row?.city || "");
  const [state, setState] = useState(row?.state || "");
  const [pincode, setPincode] = useState(row?.pincode || "");
  const [contact, setContact] = useState(row?.contact_person || "");
  const [phone, setPhone] = useState(row?.phone || "");
  const [photoRequired, setPhotoRequired] = useState(!!row?.photo_required_on_visit);
  const [active, setActive] = useState((row?.status || "active") === "active");
  const [lat, setLat] = useState(row?.geo?.lat || "");
  const [lng, setLng] = useState(row?.geo?.lng || "");
  const [radius, setRadius] = useState(row?.geo?.radius_m || 100);
  const [busy, setBusy] = useState(false);

  const center = useMemo(() => {
    const a = parseFloat(lat); const b = parseFloat(lng);
    if (!isNaN(a) && !isNaN(b)) return [a, b];
    return [20.5937, 78.9629]; // India centroid
  }, [lat, lng]);

  const detectFromAddress = async () => {
    const q = [address, city, state, pincode].filter(Boolean).join(", ");
    if (!q) return toast.error("Enter address / city first");
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
      const j = await res.json();
      if (j[0]) {
        setLat(parseFloat(j[0].lat).toFixed(6));
        setLng(parseFloat(j[0].lon).toFixed(6));
        toast.success("Coordinates found");
      } else toast.error("No results");
    } catch { toast.error("Geocoding failed"); }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const body = {
        name: name.trim(), code: code.trim() || null, type,
        address: address || null, city: city || null, state: state || null, pincode: pincode || null,
        contact_person: contact || null, phone: phone || null,
        photo_required_on_visit: photoRequired,
        status: active ? "active" : "inactive",
        geo: (lat && lng) ? { lat: parseFloat(lat), lng: parseFloat(lng), radius_m: parseInt(radius) || 100 } : null,
      };
      if (isNew) {
        // model requires company_id; backend overwrites it from the token. Pass anything.
        await api.post("/field/parties", { ...body, id: undefined, company_id: "_self_" });
      } else {
        await api.put(`/field/parties/${row.id}`, body);
      }
      toast.success(isNew ? "Party created" : "Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Mark this party inactive?")) return;
    try {
      await api.delete(`/field/parties/${row.id}`);
      toast.success("Marked inactive");
      onSaved();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="party-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add party" : "Edit party"}</DialogTitle>
          <DialogDescription>
            Clients, sites, warehouses or vendors your field team visits. Geofence defines the visit zone.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left — form */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input data-testid="party-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Code</Label>
                <Input data-testid="party-code" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="party-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PARTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input data-testid="party-address" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">City</Label>
                <Input data-testid="party-city" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input data-testid="party-state" value={state} onChange={(e) => setState(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Pincode</Label>
                <Input data-testid="party-pincode" value={pincode} onChange={(e) => setPincode(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Contact person</Label>
                <Input data-testid="party-contact" value={contact} onChange={(e) => setContact(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input data-testid="party-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2">
              <div>
                <div className="text-sm font-medium">Photo required on visit</div>
                <div className="text-xs text-zinc-500">Enforce a proof-of-visit photo before closing</div>
              </div>
              <Switch data-testid="party-photo-required" checked={photoRequired} onCheckedChange={setPhotoRequired} />
            </div>
            <div className="flex items-center justify-between bg-zinc-50 rounded px-3 py-2">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-zinc-500">Inactive parties stop auto-detecting visits</div>
              </div>
              <Switch data-testid="party-active" checked={active} onCheckedChange={setActive} />
            </div>
          </div>

          {/* Right — geofence */}
          <div className="space-y-2">
            <Label className="text-xs">Geofence — click map or auto-detect from address</Label>
            <div className="h-64 rounded border overflow-hidden">
              <MapContainer center={center} zoom={lat && lng ? 16 : 5} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
                <ClickPicker onPick={(la, ln) => { setLat(la.toFixed(6)); setLng(ln.toFixed(6)); }} />
                {lat && lng && (
                  <>
                    <Marker position={[parseFloat(lat), parseFloat(lng)]} icon={partyIcon} />
                    <Circle center={[parseFloat(lat), parseFloat(lng)]} radius={parseInt(radius) || 100} pathOptions={{ color: "#6366f1", fillOpacity: 0.15 }} />
                  </>
                )}
              </MapContainer>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Lat</Label>
                <Input data-testid="party-lat" value={lat} onChange={(e) => setLat(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Lng</Label>
                <Input data-testid="party-lng" value={lng} onChange={(e) => setLng(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Radius (m)</Label>
                <Input data-testid="party-radius" type="number" value={radius} onChange={(e) => setRadius(e.target.value)} />
              </div>
            </div>
            <Button data-testid="party-autodetect" variant="outline" className="w-full" onClick={detectFromAddress}>
              <MapPin size={14} /> Auto-detect from address
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {!isNew && (
            <Button data-testid="party-delete" variant="ghost" onClick={remove}>
              <Trash size={14} /> Mark inactive
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="party-save" onClick={save} disabled={busy}>
            {busy ? "Saving…" : isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// 2. LIVE MAP PAGE — /app/field/live-map
// ============================================================================
export function FieldLiveMap() {
  const [rows, setRows] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleOff, setModuleOff] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const [live, p] = await Promise.all([
        api.get("/field/live"),
        api.get("/field/parties").catch(() => ({ data: [] })),
      ]);
      setRows(live.data || []);
      setParties(p.data || []);
      setModuleOff(false);
    } catch (e) {
      if (e?.response?.status === 402) setModuleOff(true);
      else toast.error("Failed to load");
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, []);

  const markers = useMemo(() => rows
    .filter(r => r.last_ping?.lat && r.last_ping?.lng)
    .map(r => ({
      id: r.day_id,
      pos: [r.last_ping.lat, r.last_ping.lng],
      name: r.employee_name || r.employee_code || "Field employee",
      code: r.employee_code,
      visit: r.open_visit,
      battery: r.last_ping?.battery_pct,
      ts: r.last_ping?.ts,
      fraud: r.fraud_flags || [],
      visit_count: r.visit_count,
      day_id: r.day_id,
    })), [rows]);

  if (moduleOff) return <ModuleOffPanel title="Field — Live map" />;

  const totalKm = 0; // placeholder if we ever surface aggregate
  const fraudCount = markers.filter(m => m.fraud.length > 0).length;

  return (
    <AppShell title="Field — Live map">
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="On duty" value={String(markers.length)} testid="kpi-on-duty" hint="Active field days" />
          <StatCard label="Open visits" value={String(markers.filter(m => m.visit).length)} testid="kpi-open-visits" />
          <StatCard label="Visits today" value={String(markers.reduce((s, m) => s + (m.visit_count || 0), 0))} testid="kpi-visits-today" />
          <StatCard label="Fraud flags" value={String(fraudCount)} testid="kpi-fraud-flags" hint={fraudCount > 0 ? "Review needed" : "All clear"} />
        </div>

        <SectionCard
          title="Live tracking map"
          subtitle="Auto-refreshes every 60s. Mock-location, low-accuracy, and teleport pings are auto-flagged."
          action={
            <Button data-testid="live-map-refresh" variant="outline" onClick={load} disabled={refreshing}>
              <ArrowClockwise size={14} /> {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
          }
          testid="section-live-map"
        >
          {loading ? (
            <div className="text-sm text-zinc-500 py-6">Loading…</div>
          ) : (
            <>
              <div className="h-[480px] rounded border overflow-hidden">
                <MapContainer center={[20.5937, 78.9629]} zoom={5} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
                  {parties.filter(p => p.geo?.lat && p.geo?.lng).map(p => (
                    <Circle key={p.id} center={[p.geo.lat, p.geo.lng]} radius={p.geo.radius_m || 100}
                      pathOptions={{ color: "#a78bfa", fillOpacity: 0.08 }} />
                  ))}
                  {markers.map(m => (
                    <Marker key={m.id} position={m.pos} icon={dotIcon(m.fraud.length ? "#ef4444" : (m.visit ? "#10b981" : "#06b6d4"))}>
                      <Popup>
                        <div className="text-xs">
                          <div className="font-semibold">{m.name} {m.code && <span className="text-zinc-500">· {m.code}</span>}</div>
                          <div>Last ping: {fmtTime(m.ts)}</div>
                          {m.battery != null && <div>Battery: {m.battery}%</div>}
                          {m.visit && (
                            <div className="mt-1 text-emerald-700">
                              In visit: <b>{m.visit.party_name}</b> since {fmtTime(m.visit.arrived_at)}
                            </div>
                          )}
                          {m.fraud.length > 0 && (
                            <div className="mt-1 text-rose-700">Flags: {m.fraud.join(", ")}</div>
                          )}
                          <Link to={`/app/field/day-replay/${m.day_id}`} className="text-indigo-600 underline mt-1 inline-block">
                            View day replay →
                          </Link>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="mt-3 text-xs text-zinc-500">
                Privacy: GPS pings collected only during tracking window (HR settings). Mock-location refused. Raw pings auto-deleted after 90 days (DPDP retention).
              </div>
            </>
          )}
        </SectionCard>

        {markers.length > 0 && (
          <SectionCard title="Active field employees" testid="section-live-list">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b">
                <tr>
                  <th className="text-left py-2">Employee</th>
                  <th className="text-left py-2">Open visit</th>
                  <th className="text-left py-2">Battery</th>
                  <th className="text-left py-2">Visits today</th>
                  <th className="text-left py-2">Flags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {markers.map(m => (
                  <tr key={m.id} className="border-b last:border-b-0" data-testid={`live-row-${m.id}`}>
                    <td className="py-2">
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{m.code || "—"}</div>
                    </td>
                    <td className="py-2">{m.visit ? <span className="text-emerald-700">{m.visit.party_name}</span> : <span className="text-zinc-400">—</span>}</td>
                    <td className="py-2">{m.battery != null ? `${m.battery}%` : "—"}</td>
                    <td className="py-2">{m.visit_count || 0}</td>
                    <td className="py-2">
                      {m.fraud.length > 0
                        ? <span className="text-xs px-2 py-0.5 bg-rose-50 text-rose-700 rounded">{m.fraud.join(", ")}</span>
                        : <span className="text-xs text-emerald-700">Clean</span>}
                    </td>
                    <td className="py-2 text-right">
                      <Link to={`/app/field/day-replay/${m.day_id}`}>
                        <Button variant="ghost" size="sm"><Path size={14} /> Replay</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}
      </div>
    </AppShell>
  );
}

// ============================================================================
// 3. VISITS PAGE — /app/field/visits
// ============================================================================
export function FieldVisits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleOff, setModuleOff] = useState(false);
  const [date, setDate] = useState(() => isoDateIST());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/field/visits", { params: { date } });
      setVisits(data || []);
      setModuleOff(false);
    } catch (e) {
      if (e?.response?.status === 402) setModuleOff(true);
      else toast.error("Failed to load visits");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line

  if (moduleOff) return <ModuleOffPanel title="Field — Visits" />;

  return (
    <AppShell title="Field — Visits log">
      <SectionCard
        title="All visits"
        subtitle="Auto-detected by geofence dwell, or manually logged from the mobile app."
        action={
          <div className="flex items-center gap-2">
            <DateInput
              data-testid="visits-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
            />
            <Button data-testid="visits-refresh" variant="outline" onClick={load}>
              <ArrowClockwise size={14} />
            </Button>
          </div>
        }
        testid="section-visits"
      >
        {loading ? (
          <div className="text-sm text-zinc-500 py-6">Loading…</div>
        ) : visits.length === 0 ? (
          <div className="text-sm text-zinc-500 py-12 text-center">
            <Clock size={32} className="mx-auto text-zinc-300 mb-2" />
            No visits on this date.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 border-b">
              <tr>
                <th className="text-left py-2">Arrived</th>
                <th className="text-left py-2">Left</th>
                <th className="text-left py-2">Duration</th>
                <th className="text-left py-2">Party</th>
                <th className="text-left py-2">Detection</th>
                <th className="text-left py-2">Outcome</th>
                <th className="text-left py-2">Photo</th>
              </tr>
            </thead>
            <tbody>
              {visits.map(v => (
                <tr key={v.id} className="border-b last:border-b-0" data-testid={`visit-row-${v.id}`}>
                  <td className="py-2 text-xs">{fmtDate(v.arrived_at)}</td>
                  <td className="py-2 text-xs">{v.left_at ? fmtTime(v.left_at) : <span className="text-emerald-700">In progress</span>}</td>
                  <td className="py-2 text-xs">{v.duration_min ? `${v.duration_min} min` : "—"}</td>
                  <td className="py-2 font-medium">{v.party_name}</td>
                  <td className="py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${v.detection === "auto" ? "bg-indigo-50 text-indigo-700" : "bg-zinc-100 text-zinc-700"}`}>
                      {v.detection || "manual"}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className="text-xs capitalize">{(v.outcome || "in_progress").replace("_", " ")}</span>
                  </td>
                  <td className="py-2">
                    {v.photo_required && (v.photo_doc_ids || []).length === 0
                      ? <span className="text-xs text-rose-700"><Warning size={12} className="inline" /> Missing</span>
                      : (v.photo_doc_ids || []).length > 0
                        ? <span className="text-xs text-emerald-700"><Camera size={12} className="inline" /> {v.photo_doc_ids.length}</span>
                        : <span className="text-xs text-zinc-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </AppShell>
  );
}

// ============================================================================
// 4. DAY REPLAY — /app/field/day-replay/:dayId
// ============================================================================
export function FieldDayReplay() {
  const { dayId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moduleOff, setModuleOff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/field/days/${dayId}/replay`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (e?.response?.status === 402) setModuleOff(true);
        else toast.error("Failed to load day");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [dayId]);

  if (moduleOff) return <ModuleOffPanel title="Field — Day replay" />;
  if (loading) return <AppShell title="Day replay"><div className="text-sm text-zinc-500 py-6">Loading…</div></AppShell>;
  if (!data) return <AppShell title="Day replay"><div className="text-sm text-zinc-500 py-6">Not found.</div></AppShell>;

  const polyline = (data.polyline || []).map(p => [p.lat, p.lng]);
  const visits = data.visits || [];
  const center = polyline[0] || [20.5937, 78.9629];

  return (
    <AppShell title={`Day replay — ${fmtDate(data.day.start_at)}`}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard label="Status" value={data.day.status} testid="replay-kpi-status" />
          <StatCard label="Visits" value={String(data.day.visit_count || 0)} testid="replay-kpi-visits" />
          <StatCard label="Distance" value={fmtKm(data.day.total_km)} testid="replay-kpi-km" />
          <StatCard label="Visit time" value={`${data.day.total_minutes_visiting || 0} min`} testid="replay-kpi-visit-min" />
          <StatCard label="Pings" value={String(polyline.length)} testid="replay-kpi-pings" hint={data.day.fraud_flags?.length ? `${data.day.fraud_flags.length} flags` : "Clean"} />
        </div>

        {data.day.fraud_flags?.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-800" data-testid="replay-fraud-banner">
            <Warning size={16} className="inline mr-1" />
            Day flagged: <b>{data.day.fraud_flags.join(", ")}</b>
          </div>
        )}

        <SectionCard title="Trail and visits" subtitle="Trusted pings only (mock-location & teleport excluded)." testid="section-replay-map">
          <div className="h-[480px] rounded border overflow-hidden">
            <MapContainer center={center} zoom={polyline.length ? 14 : 5} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='© OpenStreetMap' />
              {polyline.length > 1 && <Polyline positions={polyline} pathOptions={{ color: "#6366f1", weight: 4 }} />}
              {polyline.length > 0 && (
                <>
                  <Marker position={polyline[0]} icon={dotIcon("#10b981")}><Popup>Start</Popup></Marker>
                  {polyline.length > 1 && <Marker position={polyline[polyline.length - 1]} icon={dotIcon("#ef4444")}><Popup>End</Popup></Marker>}
                </>
              )}
              {visits.map(v => v.arrived_at && data.polyline.find(p => p.visit_id === v.id) && (
                <Marker
                  key={v.id}
                  position={(() => {
                    const p = data.polyline.find(pp => pp.visit_id === v.id);
                    return p ? [p.lat, p.lng] : null;
                  })()}
                  icon={partyIcon}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-semibold">{v.party_name}</div>
                      <div>Arrived: {fmtTime(v.arrived_at)}</div>
                      <div>Left: {fmtTime(v.left_at)}</div>
                      <div>Duration: {v.duration_min || "—"} min</div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </SectionCard>

        {visits.length > 0 && (
          <SectionCard title={`Visits (${visits.length})`} testid="section-replay-visits">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b">
                <tr>
                  <th className="text-left py-2">Party</th>
                  <th className="text-left py-2">Arrived</th>
                  <th className="text-left py-2">Left</th>
                  <th className="text-left py-2">Duration</th>
                  <th className="text-left py-2">Detection</th>
                </tr>
              </thead>
              <tbody>
                {visits.map(v => (
                  <tr key={v.id} className="border-b last:border-b-0">
                    <td className="py-2 font-medium">{v.party_name}</td>
                    <td className="py-2 text-xs">{fmtTime(v.arrived_at)}</td>
                    <td className="py-2 text-xs">{fmtTime(v.left_at)}</td>
                    <td className="py-2 text-xs">{v.duration_min || "—"} min</td>
                    <td className="py-2 text-xs capitalize">{v.detection}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        )}
      </div>
    </AppShell>
  );
}

// ============================================================================
// 5. SETTINGS PAGE — /app/field/settings
// ============================================================================
const DAYS = [
  { value: "mon", label: "Mon" }, { value: "tue", label: "Tue" }, { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" }, { value: "fri", label: "Fri" }, { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
];

export function FieldSettings() {
  const [s, setS] = useState(null);
  const [moduleOff, setModuleOff] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/field/settings");
      setS(data); setModuleOff(false);
    } catch (e) {
      if (e?.response?.status === 402) setModuleOff(true);
      else toast.error("Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  if (moduleOff) return <ModuleOffPanel title="Field — Settings" />;
  if (!s) return <AppShell title="Field settings"><div className="text-sm text-zinc-500 py-6">Loading…</div></AppShell>;

  const toggleDay = (d) => {
    const cur = s.tracking_days || [];
    setS({ ...s, tracking_days: cur.includes(d) ? cur.filter(x => x !== d) : [...cur, d] });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/field/settings", s);
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell title="Field tracking — Settings">
      <div className="space-y-4 max-w-3xl">
        <SectionCard title="Module" testid="section-field-settings-module">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Enable field tracking</div>
              <div className="text-xs text-zinc-500">Turning off auto-closes all open field days for your tenant.</div>
            </div>
            <Switch data-testid="field-enabled" checked={!!s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} />
          </div>
        </SectionCard>

        <SectionCard title="Tracking window" subtitle="GPS pings are only accepted inside this window — privacy-first." testid="section-field-window">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <Label className="text-xs">Start (HH:MM, UTC)</Label>
              <Input data-testid="field-window-start" value={s.tracking_window_start_hhmm}
                onChange={(e) => setS({ ...s, tracking_window_start_hhmm: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">End (HH:MM, UTC)</Label>
              <Input data-testid="field-window-end" value={s.tracking_window_end_hhmm}
                onChange={(e) => setS({ ...s, tracking_window_end_hhmm: e.target.value })} />
            </div>
          </div>
          <Label className="text-xs">Working days</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {DAYS.map(d => (
              <button key={d.value}
                data-testid={`field-day-${d.value}`}
                onClick={() => toggleDay(d.value)}
                className={`text-xs px-3 py-1.5 rounded border ${(s.tracking_days || []).includes(d.value) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-zinc-700 border-zinc-300"}`}>
                {d.label}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <Label className="text-xs">Outside-window action</Label>
            <Select value={s.outside_window_action || "stop"}
              onValueChange={(v) => setS({ ...s, outside_window_action: v })}>
              <SelectTrigger data-testid="field-outside-action" className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stop">Stop tracking</SelectItem>
                <SelectItem value="warn">Warn only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SectionCard>

        <SectionCard title="Visit detection" subtitle="How tightly we auto-open and auto-close visits." testid="section-field-visit">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Default geofence radius (m)</Label>
              <Input data-testid="field-radius" type="number" value={s.visit_geofence_radius_m}
                onChange={(e) => setS({ ...s, visit_geofence_radius_m: parseInt(e.target.value) || 100 })} />
            </div>
            <div>
              <Label className="text-xs">Ping interval (seconds)</Label>
              <Input data-testid="field-ping-interval" type="number" value={s.ping_interval_seconds}
                onChange={(e) => setS({ ...s, ping_interval_seconds: parseInt(e.target.value) || 120 })} />
            </div>
            <div>
              <Label className="text-xs">Min visit dwell (min)</Label>
              <Input data-testid="field-dwell-min" type="number" value={s.visit_dwell_minutes_min}
                onChange={(e) => setS({ ...s, visit_dwell_minutes_min: parseInt(e.target.value) || 5 })} />
            </div>
            <div>
              <Label className="text-xs">Leave distance (m)</Label>
              <Input data-testid="field-leave-dist" type="number" value={s.visit_leave_distance_m}
                onChange={(e) => setS({ ...s, visit_leave_distance_m: parseInt(e.target.value) || 150 })} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Anti-fraud" subtitle="Industry-standard checks. Failed pings are dropped, employee day gets flagged." testid="section-field-fraud">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Refuse if mock-location detected</div>
                <div className="text-xs text-zinc-500">Recommended — fake-GPS apps cannot start a field day</div>
              </div>
              <Switch data-testid="field-refuse-mock" checked={!!s.refuse_if_mock_location}
                onCheckedChange={(v) => setS({ ...s, refuse_if_mock_location: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Accuracy floor (m)</Label>
                <Input data-testid="field-accuracy" type="number" value={s.accuracy_floor_m}
                  onChange={(e) => setS({ ...s, accuracy_floor_m: parseInt(e.target.value) || 200 })} />
              </div>
              <div>
                <Label className="text-xs">Teleport speed floor (km/h)</Label>
                <Input data-testid="field-teleport" type="number" value={s.teleport_speed_floor_kmph}
                  onChange={(e) => setS({ ...s, teleport_speed_floor_kmph: parseInt(e.target.value) || 250 })} />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Consent (DPDP)" subtitle="Employees must accept the current version once before tracking begins." testid="section-field-consent">
          <div>
            <Label className="text-xs">Consent version required</Label>
            <Input data-testid="field-consent-version" value={s.consent_version_required}
              onChange={(e) => setS({ ...s, consent_version_required: e.target.value })} className="max-w-xs" />
            <div className="text-xs text-zinc-500 mt-1">Bumping the version forces every field employee to re-accept consent on next ping.</div>
          </div>
        </SectionCard>

        <div className="flex justify-end">
          <Button data-testid="field-settings-save" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

// Default export: redirect to Parties page (most useful landing for HR)
export default function FieldOverview() {
  return <FieldParties />;
}
